/**
 * useCirurgiaMatching - Hook for matching contagem items with cirurgias
 *
 * Epic 42 Update: Simplified matching by codigo_produto + lote only
 * Returns aggregated quantities and patient names for each matching key.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import cirurgiasFaturarService, { type ItemCirurgiaFaturar } from '../services/cirurgiasFaturarService';

// Result of matching a contagem item with cirurgias
export interface CirurgiaMatch {
  totalQtd: number;
  pacientes: string[];
}

// Interface for items that can be matched
export interface MatchableItem {
  produto: string;       // codigo_produto
  lote: string;
  cnpj_cliente?: string;
  regime?: string;       // "Com Regime" | "Sem Regime" | "-"
}

export interface UseCirurgiaMatchingReturn {
  cirurgias: ItemCirurgiaFaturar[];
  matchMap: Map<string, CirurgiaMatch>;
  getMatchForItem: (item: MatchableItem) => CirurgiaMatch | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Build matching key from item properties
 * Format: "codigo_produto|lote" (simplified for better matching)
 */
const buildMatchKey = (codigoProduto: string, lote: string): string => {
  return `${codigoProduto}|${lote}`;
};

/**
 * Hook to fetch and match cirurgias with contagem items
 */
export function useCirurgiaMatching(jobId: number): UseCirurgiaMatchingReturn {
  const [cirurgias, setCirurgias] = useState<ItemCirurgiaFaturar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all cirurgias for the job
  const fetchCirurgias = useCallback(async () => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const items = await cirurgiasFaturarService.getAllItems(jobId);
      setCirurgias(items);
    } catch (err: any) {
      console.error('Error fetching cirurgias for matching:', err);
      setError(err.message || 'Erro ao carregar cirurgias');
      setCirurgias([]);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  // Load cirurgias on mount and when jobId changes
  useEffect(() => {
    fetchCirurgias();
  }, [fetchCirurgias]);

  // Build match map from cirurgias
  // Key: "codigo_produto|lote"
  // Value: { totalQtd, pacientes[] }
  const matchMap = useMemo(() => {
    const map = new Map<string, CirurgiaMatch>();

    cirurgias.forEach((cirurgia) => {
      const key = buildMatchKey(cirurgia.codigo_produto, cirurgia.lote);

      const existing = map.get(key);
      const qty = parseFloat(cirurgia.quantidade) || 0;

      if (existing) {
        existing.totalQtd += qty;
        // Add patient if not already in list
        if (cirurgia.paciente && !existing.pacientes.includes(cirurgia.paciente)) {
          existing.pacientes.push(cirurgia.paciente);
        }
      } else {
        map.set(key, {
          totalQtd: qty,
          pacientes: cirurgia.paciente ? [cirurgia.paciente] : [],
        });
      }
    });

    return map;
  }, [cirurgias]);

  // Get match for a specific contagem item
  const getMatchForItem = useCallback(
    (item: MatchableItem): CirurgiaMatch | null => {
      if (!item.produto || !item.lote) {
        return null;
      }

      // Simple match by codigo_produto + lote
      const key = buildMatchKey(item.produto, item.lote);
      return matchMap.get(key) || null;
    },
    [matchMap]
  );

  return {
    cirurgias,
    matchMap,
    getMatchForItem,
    loading,
    error,
    refetch: fetchCirurgias,
  };
}
